<template>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">用户管理</h1>
        <div class="flex space-x-4">
          <div class="relative">
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="搜索用户名、姓名..." 
              class="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <svg class="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
          <select 
            v-model="roleFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">全部角色</option>
            <option value="admin">管理员</option>
            <option value="manager">部门管理员</option>
            <option value="employee">普通员工</option>
          </select>
          <select 
            v-model="departmentFilter" 
            class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">全部部门</option>
            <option value="tech">技术部</option>
            <option value="hr">人力资源部</option>
            <option value="finance">财务部</option>
            <option value="marketing">市场部</option>
            <option value="sales">销售部</option>
          </select>
          <button 
            @click="createUser"
            class="px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors duration-300 flex items-center"
          >
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            新增用户
          </button>
        </div>
      </div>

      <!-- 用户列表 -->
      <div class="overflow-hidden shadow-md border border-gray-200 rounded-lg">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">用户名</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">姓名</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">部门</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">角色</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">邮箱</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">状态</th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(user, index) in filteredUsers" :key="index" class="hover:bg-gray-50 transition-colors duration-200">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="w-10 h-10 flex-shrink-0 mr-3">
                    <div class="rounded-full bg-red-100 h-10 w-10 flex items-center justify-center">
                      <span class="text-red-600 font-medium">{{ user.name.charAt(0) }}</span>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">{{ user.username }}</div>
                    <div class="text-sm text-gray-500">ID: {{ user.id }}</div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.name }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ departmentText(user.department) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span 
                  :class="[
                    'px-2 py-1 inline-flex text-xs font-semibold rounded-full',
                    user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                    user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  ]"
                >
                  {{ roleText(user.role) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ user.email }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span 
                  :class="[
                    'px-2 py-1 inline-flex text-xs font-semibold rounded-full',
                    user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ statusText(user.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <button 
                  @click="editUser(user)" 
                  class="text-blue-600 hover:text-blue-800 mr-3"
                >
                  编辑
                </button>
                <button 
                  @click="toggleStatus(user)" 
                  :class="user.status === 'active' ? 'text-gray-600 hover:text-gray-800' : 'text-green-600 hover:text-green-800'"
                >
                  {{ user.status === 'active' ? '停用' : '启用' }}
                </button>
                <button 
                  @click="resetPassword(user)" 
                  class="text-yellow-600 hover:text-yellow-800 ml-3"
                >
                  重置密码
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <div class="flex justify-between items-center mt-6">
        <p class="text-sm text-gray-700">
          显示 <span class="font-medium">1</span> 至 <span class="font-medium">10</span> 条，共 <span class="font-medium">{{ filteredUsers.length }}</span> 条
        </p>
        <div class="flex space-x-2">
          <button 
            @click="previousPage" 
            :disabled="currentPage === 1" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === 1 ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            上一页
          </button>
          <button 
            v-for="page in totalPages"
            :key="page"
            @click="goToPage(page)" 
            :class="[
              'px-3 py-1 border rounded-md', 
              currentPage === page ? 'bg-red-500 text-white border-red-500' : 'border-gray-300 text-gray-700'
            ]"
          >
            {{ page }}
          </button>
          <button 
            @click="nextPage" 
            :disabled="currentPage === totalPages" 
            :class="[
              'px-3 py-1 border rounded-md',
              currentPage === totalPages ? 'cursor-not-allowed text-gray-400 border-gray-200' : 'border-gray-300 text-gray-700'
            ]"
          >
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 用户表单对话框 -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg max-w-2xl w-full">
        <!-- 对话框头部 -->
        <div class="flex justify-between items-center border-b px-6 py-4">
          <h3 class="text-lg font-semibold text-gray-800">{{ isEdit ? '编辑用户' : '新增用户' }}</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-500">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <!-- 对话框内容 -->
        <div class="px-6 py-4">
          <form @submit.prevent="submitForm">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label for="username" class="block text-sm font-medium text-gray-700 mb-1">用户名</label>
                <input 
                  type="text" 
                  id="username" 
                  v-model="form.username" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                  :disabled="isEdit"
                />
              </div>
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-1">姓名</label>
                <input 
                  type="text" 
                  id="name" 
                  v-model="form.name" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
            </div>
            
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label for="department" class="block text-sm font-medium text-gray-700 mb-1">部门</label>
                <select 
                  id="department" 
                  v-model="form.department" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="tech">技术部</option>
                  <option value="hr">人力资源部</option>
                  <option value="finance">财务部</option>
                  <option value="marketing">市场部</option>
                  <option value="sales">销售部</option>
                </select>
              </div>
              <div>
                <label for="role" class="block text-sm font-medium text-gray-700 mb-1">角色</label>
                <select 
                  id="role" 
                  v-model="form.role" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="admin">管理员</option>
                  <option value="manager">部门管理员</option>
                  <option value="employee">普通员工</option>
                </select>
              </div>
            </div>

            <div class="mb-4">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-1">邮箱</label>
              <input 
                type="email" 
                id="email" 
                v-model="form.email" 
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>

            <div class="mb-4" v-if="!isEdit">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">初始密码</label>
              <input 
                type="password" 
                id="password" 
                v-model="form.password" 
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>

            <div class="mb-4">
              <label for="status" class="block text-sm font-medium text-gray-700 mb-1">状态</label>
              <div class="flex space-x-4">
                <div class="flex items-center">
                  <input id="active" name="status" type="radio" v-model="form.status" value="active" class="h-4 w-4 text-red-600 focus:ring-red-500" />
                  <label for="active" class="ml-2 block text-sm text-gray-700">启用</label>
                </div>
                <div class="flex items-center">
                  <input id="inactive" name="status" type="radio" v-model="form.status" value="inactive" class="h-4 w-4 text-red-600 focus:ring-red-500" />
                  <label for="inactive" class="ml-2 block text-sm text-gray-700">停用</label>
                </div>
              </div>
            </div>

            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button 
                type="button" 
                @click="closeModal" 
                class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                取消
              </button>
              <button 
                type="submit" 
                class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                {{ isEdit ? '保存' : '创建' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserManagement',
  data() {
    return {
      searchQuery: '',
      roleFilter: 'all',
      departmentFilter: 'all',
      currentPage: 1,
      itemsPerPage: 10,
      showModal: false,
      isEdit: false,
      form: {
        username: '',
        name: '',
        department: 'tech',
        role: 'employee',
        email: '',
        password: '',
        status: 'active'
      },
      users: [
        {
          id: 1,
          username: 'admin',
          name: '系统管理员',
          department: 'tech',
          role: 'admin',
          email: 'admin@example.com',
          status: 'active'
        },
        {
          id: 2,
          username: 'tech_manager',
          name: '张经理',
          department: 'tech',
          role: 'manager',
          email: 'tech_manager@example.com',
          status: 'active'
        },
        {
          id: 3,
          username: 'hr_manager',
          name: '李经理',
          department: 'hr',
          role: 'manager',
          email: 'hr_manager@example.com',
          status: 'active'
        },
        {
          id: 4,
          username: 'zhangsan',
          name: '张三',
          department: 'tech',
          role: 'employee',
          email: 'zhangsan@example.com',
          status: 'active'
        },
        {
          id: 5,
          username: 'lisi',
          name: '李四',
          department: 'sales',
          role: 'employee',
          email: 'lisi@example.com',
          status: 'active'
        },
        {
          id: 6,
          username: 'wangwu',
          name: '王五',
          department: 'finance',
          role: 'employee',
          email: 'wangwu@example.com',
          status: 'inactive'
        },
        {
          id: 7,
          username: 'zhaoliu',
          name: '赵六',
          department: 'marketing',
          role: 'employee',
          email: 'zhaoliu@example.com',
          status: 'active'
        }
      ]
    }
  },
  computed: {
    filteredUsers() {
      let result = this.users;
      
      // 搜索过滤
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(user => 
          user.username.toLowerCase().includes(query) || 
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
        );
      }
      
      // 角色过滤
      if (this.roleFilter !== 'all') {
        result = result.filter(user => user.role === this.roleFilter);
      }
      
      // 部门过滤
      if (this.departmentFilter !== 'all') {
        result = result.filter(user => user.department === this.departmentFilter);
      }
      
      return result;
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    }
  },
  methods: {
    departmentText(department) {
      const departmentMap = {
        'tech': '技术部',
        'hr': '人力资源部',
        'finance': '财务部',
        'marketing': '市场部',
        'sales': '销售部'
      };
      return departmentMap[department] || department;
    },
    roleText(role) {
      const roleMap = {
        'admin': '系统管理员',
        'manager': '部门管理员',
        'employee': '普通员工'
      };
      return roleMap[role] || role;
    },
    statusText(status) {
      const statusMap = {
        'active': '已启用',
        'inactive': '已停用'
      };
      return statusMap[status] || status;
    },
    createUser() {
      this.isEdit = false;
      this.form = {
        username: '',
        name: '',
        department: 'tech',
        role: 'employee',
        email: '',
        password: '',
        status: 'active'
      };
      this.showModal = true;
    },
    editUser(user) {
      this.isEdit = true;
      this.form = {
        username: user.username,
        name: user.name,
        department: user.department,
        role: user.role,
        email: user.email,
        status: user.status
      };
      this.showModal = true;
    },
    toggleStatus(user) {
      user.status = user.status === 'active' ? 'inactive' : 'active';
      alert(`用户 ${user.name} 已${user.status === 'active' ? '启用' : '停用'}`);
    },
    resetPassword(user) {
      // 这里应该调用后端API重置密码
      alert(`用户 ${user.name} 的密码已重置为默认密码，请通知用户尽快修改`);
    },
    closeModal() {
      this.showModal = false;
    },
    submitForm() {
      if (this.isEdit) {
        // 更新现有用户
        const index = this.users.findIndex(user => user.username === this.form.username);
        if (index !== -1) {
          this.users[index] = {
            ...this.users[index],
            name: this.form.name,
            department: this.form.department,
            role: this.form.role,
            email: this.form.email,
            status: this.form.status
          };
        }
        alert('用户信息更新成功');
      } else {
        // 添加新用户
        // 检查用户名是否已存在
        if (this.users.some(user => user.username === this.form.username)) {
          alert('用户名已存在，请使用其他用户名');
          return;
        }
        
        const newUser = {
          id: this.users.length + 1,
          username: this.form.username,
          name: this.form.name,
          department: this.form.department,
          role: this.form.role,
          email: this.form.email,
          status: this.form.status
        };
        
        this.users.push(newUser);
        alert('用户创建成功');
      }
      
      this.closeModal();
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    goToPage(page) {
      this.currentPage = page;
    }
  }
}
</script> 